import { withErrorHandler } from 'services/Api';
import type { ProxyRequest, ProxyResponse, BirdProxyClientError, ProxyResponseBirdError } from './typesProxy';
import { toast } from 'react-toastify';
import { getTokenPrefix, TokenPrefix } from 'lib/utils/embedded-app.utils';

export class BirdProxyClient {
    /**
     * Async function for retrieving the access token.
     * Must be set from outside: proxyClient.getAccessTokenSilently = ...
     */
    public getAccessTokenSilently?: () => Promise<string>;

    private readonly tokenPrefix: TokenPrefix = getTokenPrefix();

    async fetch<Res = any>(
        req: ProxyRequest & { method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' },
        businessAccountId: string,
    ): Promise<Res | void> {
        const headers = await this.getHeaders();
        try {
            const res = await fetch(`/api/business-accounts/${businessAccountId}/bird-proxy`, {
                method: 'POST',
                headers,
                body: JSON.stringify(req),
            });

            if (res.status === 401) {
                console.error('401 - reloading');
                window.location.reload();
                throw new BirdProxyClientErr({
                    status: 401,
                    code: 'Unauthorized',
                    message: res.statusText || 'Invalid or expired token',
                    details: null,
                });
            }

            const data = (await res.json()) as ProxyResponse<Res>;

            if (res.status === 200) {
                if (data.status >= 400) {
                    const birdApiError = data as ProxyResponseBirdError;
                    return await withErrorHandler(async () => {
                        toast.error(
                            `Error ${birdApiError.status}: ${birdApiError.birdResponse.code ?? birdApiError.birdResponse.message ?? 'Bird API rejection'}`,
                        );
                        throw new BirdProxyClientErr({
                            status: birdApiError.status,
                            code: birdApiError.birdResponse.code ?? 'Bird API rejection',
                            message: birdApiError.birdResponse.message ?? 'Bird API rejection',
                            details: birdApiError.birdResponse.details ?? data ?? null,
                        });
                    });
                }

                if (BirdProxyClient.isProxySuccessResponse(data)) {
                    return data.birdResponse;
                }
            }

            return await withErrorHandler(async () => {
                const message =
                    typeof data === 'object'
                        ? (data as any).detail || (data as any).message || res.statusText || 'Proxy rejection'
                        : 'Proxy rejection';
                toast.error(`Error ${res.status}: ${message}`);
                throw new BirdProxyClientErr({
                    status: res.status,
                    code: null,
                    message: message,
                    details: data,
                });
            });
        } catch (err) {
            const birdApiError = err as BirdProxyClientError;
            if (birdApiError?.status) {
                toast.error(
                    `Error ${birdApiError.status}: ${birdApiError.code ?? birdApiError.message ?? 'Bird API rejection'}`,
                );
            } else {
                toast.error(`Error ${(err as Error)?.name}: ${(err as Error)?.message ?? 'Something went wrong'}`);
            }
            return await withErrorHandler(async () => {
                throw new BirdProxyClientErr({
                    status: (err as BirdProxyClientError)?.status ?? null,
                    code: (err as BirdProxyClientError)?.code ?? (err as Error).name ?? 'Unknown error',
                    message: (err as BirdProxyClientError)?.message ?? (err as Error).name ?? 'Something went wrong',
                    details: (err as BirdProxyClientError)?.details ?? err ?? null,
                });
            });
        }
    }

    async get<Res = any>(req: ProxyRequest, businessAccountId: string): Promise<Res | void> {
        return this.fetch({ ...req, method: 'GET' }, businessAccountId);
    }

    async post<Res = any>(req: ProxyRequest, businessAccountId: string): Promise<Res | void> {
        return this.fetch({ ...req, method: 'POST' }, businessAccountId);
    }

    async patch<Res = any>(req: ProxyRequest, businessAccountId: string): Promise<Res | void> {
        return this.fetch({ ...req, method: 'PATCH' }, businessAccountId);
    }

    async put<Res = any>(req: ProxyRequest, businessAccountId: string): Promise<Res | void> {
        return this.fetch({ ...req, method: 'PUT' }, businessAccountId);
    }

    async delete<Res = any>(req: ProxyRequest, businessAccountId: string): Promise<Res | void> {
        return this.fetch({ ...req, method: 'DELETE' }, businessAccountId);
    }

    protected async getHeaders(): Promise<{
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Content-Type': string;
        authorization: string;
    }> {
        if (!this.getAccessTokenSilently) {
            throw new Error('proxyClient.getAccessTokenSilently is not set! Set this function before first use.');
        }
        const accessToken = await this.getAccessTokenSilently();
        return {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'Content-Type': 'application/json',
            authorization: `${this.tokenPrefix} ${accessToken}`,
        };
    }

    static isProxySuccessResponse(obj: any): obj is ProxyResponse {
        return (
            obj &&
            typeof obj === 'object' &&
            typeof obj.status === 'number' &&
            [200, 201, 204].includes(obj.status) &&
            'birdResponse' in obj &&
            typeof obj.birdResponse === 'object' &&
            'birdHeaders' in obj &&
            typeof obj.birdHeaders === 'object'
        );
    }

    static isBirdProxyClientError(obj: any): obj is BirdProxyClientError {
        return (
            obj &&
            typeof obj === 'object' &&
            (typeof obj.status === 'number' || typeof obj.status === 'string') &&
            +obj.status >= 400 &&
            typeof obj.code === 'string' &&
            typeof obj.message === 'string'
        );
    }
}

export const proxyClient = new BirdProxyClient();

export class BirdProxyClientErr extends Error {
    code: string | number | null;

    status: string | number | null;

    details: any;

    message: string;

    constructor({
        code,
        status,
        message,
        details,
    }: {
        code: string | number | null;
        status: string | number | null;
        message: string;
        details: any;
    }) {
        super(message);
        this.code = code;
        this.message = message;
        this.status = status;
        this.details = details;
    }
}
